// Settings modal — one home for everything that isn't the moment-to-moment
// performance: How it works, Songs, Calibrate, Export, plus the master Mix and
// Advanced (loop-first / layer length). Keeps the main view focused.

import { createDialog, type Dialog } from "./dialog.ts";
import { ICONS } from "./icons.ts";
import { createSwitch } from "./controls.ts";

export interface SettingsHandlers {
  onHowItWorks(): void;
  onSongs(): void;
  onExport(): void;
  onShare(): void;
  onLoopFirst(on: boolean): void;
  onMultiple(multiple: number): void;
}

export interface Settings {
  element: HTMLElement;
  open(): void;
}

const MULTIPLES = [1, 2, 4] as const;

export function createSettings(handlers: SettingsHandlers, mixEl: HTMLElement): Settings {
  const dialog: Dialog = createDialog("Settings");

  // 4 launcher cards
  const grid = document.createElement("div");
  grid.className = "settings-grid";
  grid.append(
    card("help", "How it works", "A quick guided walkthrough", () => {
      dialog.close();
      handlers.onHowItWorks();
    }),
    card("songs", "Songs", "Open, rename, or delete sessions", () => {
      dialog.close();
      handlers.onSongs();
    }),
    card("download", "Export", "WAV or MP3, full mix or stems", () => {
      dialog.close();
      handlers.onExport();
    }),
    card("share", "Share link", "Copy a link to this loop", () => {
      dialog.close();
      handlers.onShare();
    }),
  );

  // Mix section (master strip moved in here)
  const mix = section("Mix");
  mix.append(mixEl);

  // Advanced section
  const adv = section("Advanced");
  const advRow = document.createElement("div");
  advRow.className = "settings-adv";

  const loopFirst = createSwitch(
    "Loop-first length",
    false,
    (on) => handlers.onLoopFirst(on),
    "Let your first take define the loop length",
  ).el;

  const multLabel = document.createElement("span");
  multLabel.className = "settings-adv__label";
  multLabel.textContent = "Next layer length";
  const seg = document.createElement("div");
  seg.className = "seg";
  for (const m of MULTIPLES) {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "seg__btn";
    b.textContent = `${m}×`;
    b.setAttribute("aria-pressed", String(m === 1));
    if (m === 1) b.classList.add("is-active");
    b.addEventListener("click", () => {
      for (const x of seg.children) {
        x.classList.toggle("is-active", x === b);
        x.setAttribute("aria-pressed", String(x === b));
      }
      handlers.onMultiple(m);
    });
    seg.append(b);
  }
  advRow.append(loopFirst, multLabel, seg);
  adv.append(advRow);

  dialog.body.append(grid, mix, adv);

  return {
    element: dialog.element,
    open: () => dialog.open(),
  };
}

function card(icon: string, title: string, desc: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = "settings-card";
  b.innerHTML = `<span class="settings-card__icon">${ICONS[icon] ?? ""}</span><span class="settings-card__title">${title}</span><span class="settings-card__desc">${desc}</span>`;
  b.addEventListener("click", onClick);
  return b;
}

function section(title: string): HTMLElement {
  const el = document.createElement("div");
  el.className = "settings-section";
  const h = document.createElement("h3");
  h.className = "settings-section__title";
  h.textContent = title;
  el.append(h);
  return el;
}
